import styled from "@emotion/styled";
import { Colors, Input } from "@snapchat/snap-design-system";
import { StatusType } from "@snapchat/snap-design-system/lib/components/Control/types";
import { type FC, useCallback, useEffect, useRef, useState } from "react";
import { CopyToClipboardButton } from "../../../common/components";
import { ellipsisLeft } from "../../../common/utils/ellipsisLeft";

type CopyInput = {
    value: string;
    label: string;
    statusType?: StatusType;
};

export const CopyInput: FC<CopyInput> = ({ value, label, statusType }) => {
    const [inputDivWidth, setInputDivWidth] = useState<number>();
    const [valToShow, setValToShow] = useState<string>(value);

    const resizeObserver = useRef<ResizeObserver>(
        new ResizeObserver((entries: ResizeObserverEntry[]) => {
            setInputDivWidth(entries[0]?.contentRect?.width);
        }),
    );

    const inputDivRef = useCallback((node: HTMLDivElement) => {
        if (node != null) {
            resizeObserver?.current.observe(node);
        } else {
            resizeObserver?.current.disconnect();
        }
    }, []);

    useEffect(() => {
        if (inputDivWidth && value) {
            // measured
            const avgPxPerChar = 8;
            const charsToFit = inputDivWidth / avgPxPerChar;
            // the overflow buffer is estimated to be 8
            setValToShow(ellipsisLeft(value, charsToFit, 8));
        }
    }, [inputDivWidth, value]);

    return (
        <RelativeContainer>
            <div ref={inputDivRef}>
                <StyledInput
                    label={label}
                    value={valToShow}
                    statusType={statusType}
                    data-testid={label.split(" ").join("-").toLocaleLowerCase()}
                />
            </div>
            <AbsoluteContainer>
                <CopyToClipboardButton
                    tooltip={`Copy ${label}`}
                    copyValue={value}
                    data-testid="app-copy-field"
                    notification={{ title: `${label} copied to clipboard` }}
                    testId={label.split(" ").join(".").toLocaleLowerCase()}
                />
            </AbsoluteContainer>
        </RelativeContainer>
    );
};

const RelativeContainer = styled.div`
    position: relative;
    flex-basis: 40%;
`;

const AbsoluteContainer = styled.div`
    position: absolute;
    z-index: 10;
    top: 45%;
    right: 2px;
`;

const StyledInput = styled(Input)`
    input {
        padding-right: 9px !important;
        color: ${Colors.Gray.V300} !important;
        caret-color: transparent;
    }
`;
